import { useEffect, useState, type FormEvent } from "react";
import {
  ApiError,
  activateTeamMember,
  deleteTeamMember,
  editTeamMember,
  inviteTeamMember,
  listTeam,
  revokeTeamMember,
  updateTeamRole,
  type TeamMember,
  type TeamInviteResponse,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate, formatRelative } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, type ColumnDef } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import { TypedConfirmModal } from "@/components/TypedConfirmModal";
import {
  UserPlus,
  Ban,
  KeyRound,
  Mail,
  Link2,
  Copy,
  CheckCircle2,
  Pencil,
  Trash2,
  PlayCircle,
  MoreVertical,
} from "lucide-react";

const ROLE_LABELS: Record<TeamMember["role"], string> = {
  merchant_admin: "Admin marchand",
  finance: "Finance",
  it: "Informatique",
  commercial: "Commercial",
};

const ROLE_COLORS: Record<TeamMember["role"], "primary" | "info" | "warning" | "success"> = {
  merchant_admin: "primary",
  finance: "info",
  it: "warning",
  commercial: "success",
};

type ActionKind = "edit" | "deactivate" | "activate" | "delete";

export function MerchantTeamPage() {
  const auth = useAuth();
  const me = auth.status === "authenticated" ? auth.user : null;
  const isAdmin = me?.role === "merchant_admin";

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  // Action modals
  const [editTarget, setEditTarget] = useState<TeamMember | null>(null);
  const [confirmKind, setConfirmKind] = useState<ActionKind | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<TeamMember | null>(null);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listTeam();
      setMembers(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChangeRole = async (m: TeamMember, newRole: TeamMember["role"]) => {
    if (newRole === m.role) return;
    if (!confirm(`Changer le rôle de ${m.email} en ${ROLE_LABELS[newRole]} ?`)) return;
    await updateTeamRole(m.id, newRole);
    await load();
  };

  const openConfirm = (kind: ActionKind, m: TeamMember) => {
    setConfirmTarget(m);
    setConfirmKind(kind);
    setActionMenuId(null);
  };

  const handleConfirm = async () => {
    if (!confirmTarget || !confirmKind) return;
    if (confirmKind === "deactivate") await revokeTeamMember(confirmTarget.id);
    else if (confirmKind === "activate") await activateTeamMember(confirmTarget.id);
    else if (confirmKind === "delete") await deleteTeamMember(confirmTarget.id);
    setConfirmKind(null);
    setConfirmTarget(null);
    await load();
  };

  const columns: ColumnDef<TeamMember>[] = [
    {
      key: "name",
      header: "Membre",
      minWidth: 280,
      sortable: true,
      accessor: (m) => m.email,
      cell: (m) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-brand-50 text-brand-700 font-bold flex items-center justify-center text-xs shrink-0">
            {((m.first_name?.[0] ?? "") + (m.last_name?.[0] ?? "")).toUpperCase() || m.email[0].toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-[#181c32]">
              {[m.first_name, m.last_name].filter(Boolean).join(" ") || m.email}
              {me?.id === m.id && <span className="ml-1.5 text-[10px] text-brand-700 font-bold">(vous)</span>}
            </div>
            <div className="text-xs text-[#7e8299] truncate">{m.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      header: "Rôle",
      minWidth: 160,
      cell: (m) => {
        if (isAdmin && me?.id !== m.id) {
          return (
            <select
              value={m.role}
              onChange={(e) => onChangeRole(m, e.target.value as TeamMember["role"])}
              className="bg-[#f5f8fa] rounded-md px-2.5 py-1.5 text-xs font-semibold focus:outline-none"
            >
              {Object.entries(ROLE_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          );
        }
        return <Badge color={ROLE_COLORS[m.role]}>{ROLE_LABELS[m.role]}</Badge>;
      },
    },
    {
      key: "status",
      header: "Statut",
      minWidth: 100,
      cell: (m) => <Badge color={m.status === "active" ? "success" : "danger"}>{m.status}</Badge>,
    },
    {
      key: "last_login",
      header: "Dernière connexion",
      minWidth: 160,
      cell: (m) =>
        m.last_login_at ? (
          <span className="text-xs text-[#3f4254]" title={formatDate(m.last_login_at)}>
            {formatRelative(m.last_login_at)}
          </span>
        ) : (
          <span className="text-xs text-[#7e8299]">Jamais</span>
        ),
    },
    {
      key: "created_at",
      header: "Ajouté le",
      minWidth: 130,
      cell: (m) => <span className="text-xs text-[#7e8299]">{formatDate(m.created_at)}</span>,
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            header: "Actions",
            align: "right" as const,
            minWidth: 130,
            cell: (m: TeamMember) =>
              me?.id === m.id ? (
                <span className="text-xs text-[#a1a5b7]">—</span>
              ) : (
                <div className="relative inline-block">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActionMenuId(actionMenuId === m.id ? null : m.id);
                    }}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-[#7e8299] hover:bg-[#f5f8fa] hover:text-[#181c32]"
                    aria-label="Actions"
                  >
                    <MoreVertical size={16} />
                  </button>
                  {actionMenuId === m.id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setActionMenuId(null)} />
                      <div className="absolute right-0 mt-1 w-52 bg-white rounded-xl shadow-dropdown border border-[#eff2f5] py-1.5 z-20">
                        <ActionMenuItem
                          icon={<Pencil size={14} />}
                          label="Éditer"
                          onClick={() => {
                            setEditTarget(m);
                            setActionMenuId(null);
                          }}
                        />
                        {m.status === "active" ? (
                          <ActionMenuItem
                            icon={<Ban size={14} />}
                            label="Désactiver"
                            tone="warning"
                            onClick={() => openConfirm("deactivate", m)}
                          />
                        ) : (
                          <ActionMenuItem
                            icon={<PlayCircle size={14} />}
                            label="Réactiver"
                            tone="success"
                            onClick={() => openConfirm("activate", m)}
                          />
                        )}
                        <div className="border-t border-[#eff2f5] my-1" />
                        <ActionMenuItem
                          icon={<Trash2 size={14} />}
                          label="Supprimer"
                          tone="danger"
                          onClick={() => openConfirm("delete", m)}
                        />
                      </div>
                    </>
                  )}
                </div>
              ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title="Mon équipe"
        breadcrumbs={[{ label: "Mon espace" }, { label: "Équipe" }]}
        actions={
          isAdmin && (
            <button onClick={() => setInviteOpen(true)} className="btn btn-primary btn-sm">
              <UserPlus size={14} />
              Inviter un membre
            </button>
          )
        }
      />

      <DataTable<TeamMember>
        title={`${members.length} membre${members.length > 1 ? "s" : ""}`}
        subtitle={isAdmin ? "Gérez les rôles et accès de votre équipe" : "Liste de l'équipe (lecture seule)"}
        searchable
        searchPlaceholder="Rechercher par email ou nom…"
        columns={columns}
        data={members}
        rowKey={(m) => m.id}
        loading={loading}
      />

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} onCreated={() => load()} />

      {editTarget && (
        <EditModal
          member={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            load();
          }}
        />
      )}

      {confirmKind === "deactivate" && confirmTarget && (
        <TypedConfirmModal
          open
          onClose={() => { setConfirmKind(null); setConfirmTarget(null); }}
          onConfirm={handleConfirm}
          title="Désactiver le compte"
          confirmWord="désactiver"
          confirmLabel="Désactiver"
          variant="warning"
          body={
            <>
              Vous êtes sur le point de désactiver <strong>{confirmTarget.email}</strong>. Cet utilisateur ne
              pourra plus se connecter au dashboard tant que vous ne le réactiverez pas. Toutes ses sessions
              actives expireront au prochain refresh token.
            </>
          }
        />
      )}

      {confirmKind === "activate" && confirmTarget && (
        <TypedConfirmModal
          open
          onClose={() => { setConfirmKind(null); setConfirmTarget(null); }}
          onConfirm={handleConfirm}
          title="Réactiver le compte"
          confirmWord="activer"
          confirmLabel="Réactiver"
          variant="primary"
          body={
            <>
              Réactivation de <strong>{confirmTarget.email}</strong>. L'utilisateur pourra à nouveau se
              connecter au dashboard avec son mot de passe existant.
            </>
          }
        />
      )}

      {confirmKind === "delete" && confirmTarget && (
        <TypedConfirmModal
          open
          onClose={() => { setConfirmKind(null); setConfirmTarget(null); }}
          onConfirm={handleConfirm}
          title="Supprimer définitivement le compte"
          confirmWord="supprimer"
          confirmLabel="Supprimer définitivement"
          variant="danger"
          body={
            <>
              Cette action est <strong>irréversible</strong>. Le compte de <strong>{confirmTarget.email}</strong>{" "}
              sera <strong>définitivement supprimé</strong> ainsi que toutes ses sessions et préférences.
              L'historique d'audit log est conservé. Pour une suppression réversible, utilisez plutôt « Désactiver ».
            </>
          }
        />
      )}
    </div>
  );
}

function ActionMenuItem({
  icon,
  label,
  tone = "default",
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  tone?: "default" | "warning" | "danger" | "success";
  onClick: () => void;
}) {
  const cls = {
    default: "text-[#3f4254] hover:bg-[#f5f8fa]",
    warning: "text-warning-700 hover:bg-warning-50",
    danger: "text-danger-700 hover:bg-danger-50",
    success: "text-success-700 hover:bg-success-50",
  }[tone];
  return (
    <button onClick={onClick} className={`w-full px-3 py-2 text-left text-sm font-medium flex items-center gap-2.5 ${cls}`}>
      {icon}
      {label}
    </button>
  );
}

function EditModal({ member, onClose, onSaved }: { member: TeamMember; onClose: () => void; onSaved: () => void }) {
  const [firstName, setFirstName] = useState(member.first_name ?? "");
  const [lastName, setLastName] = useState(member.last_name ?? "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await editTeamMember(member.id, { first_name: firstName, last_name: lastName });
      onSaved();
    } catch (err: any) {
      setError(err?.detail || err?.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Éditer le membre"
      subtitle={member.email}
      size="md"
      closeOnOverlay={!loading}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={loading} className="btn btn-light btn-sm">
            Annuler
          </button>
          <button type="submit" form="edit-form" disabled={loading} className="btn btn-primary btn-sm">
            {loading ? "Enregistrement…" : "Enregistrer"}
          </button>
        </>
      }
    >
      <form id="edit-form" onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Prénom</label>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="form-input" maxLength={100} />
          </div>
          <div>
            <label className="form-label">Nom</label>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="form-input" maxLength={100} />
          </div>
        </div>
        <div className="text-xs text-[#7e8299] leading-relaxed">
          L'email et le rôle ne sont pas modifiables ici. Pour changer le rôle, utilisez le sélecteur dans la table.
        </div>
        {error && <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3">{error}</div>}
      </form>
    </Modal>
  );
}

function InviteModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<TeamMember["role"]>("commercial");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<TeamInviteResponse | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const reset = () => {
    setEmail("");
    setFirstName("");
    setLastName("");
    setRole("commercial");
    setLoading(false);
    setError(null);
    setCreated(null);
    setCopied(null);
  };

  const handleClose = () => {
    if (loading) return;
    if (created) onCreated();
    reset();
    onClose();
  };

  const copy = async (key: string, val: string) => {
    await navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const r = await inviteTeamMember({
        email: email.trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        role,
      });
      setCreated(r);
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError("Erreur");
    } finally {
      setLoading(false);
    }
  };

  if (created) {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        title="Membre invité"
        subtitle={created.email}
        size="md"
        closeOnOverlay={false}
        footer={
          <button onClick={handleClose} className="btn btn-primary btn-sm">
            Terminer
          </button>
        }
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-success-50 border-l-4 border-success-500 px-4 py-3 flex items-center gap-3">
            <CheckCircle2 size={20} className="text-success-700" />
            <div>
              <div className="font-bold text-success-700 text-sm">Compte créé</div>
              <div className="text-xs text-success-700">Transmettez ces credentials par canal sécurisé.</div>
            </div>
          </div>
          <CredentialRow icon={<Mail size={14} />} label="Email" value={created.email} k="em" copied={copied} onCopy={copy} />
          <CredentialRow icon={<KeyRound size={14} />} label="Mot de passe temporaire" value={created.temp_password} k="pw" copied={copied} onCopy={copy} />
          <CredentialRow icon={<Link2 size={14} />} label="Lien de connexion" value={created.login_url} k="ln" copied={copied} onCopy={copy} />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Inviter un membre"
      subtitle="Un compte sera créé avec un mot de passe temporaire"
      size="md"
      closeOnOverlay={!loading}
      footer={
        <>
          <button type="button" onClick={handleClose} disabled={loading} className="btn btn-light btn-sm">
            Annuler
          </button>
          <button type="submit" form="invite-form" disabled={loading} className="btn btn-primary btn-sm">
            <UserPlus size={14} />
            {loading ? "Création…" : "Créer le compte"}
          </button>
        </>
      }
    >
      <form id="invite-form" onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="form-label">Email *</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="form-input" placeholder="prenom@votreentreprise.com" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Prénom</label>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="form-input" maxLength={100} />
          </div>
          <div>
            <label className="form-label">Nom</label>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="form-input" maxLength={100} />
          </div>
        </div>
        <div>
          <label className="form-label">Rôle</label>
          <select value={role} onChange={(e) => setRole(e.target.value as TeamMember["role"])} className="form-input">
            {Object.entries(ROLE_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
          <div className="form-hint">
            Voir la page <strong>Rôles et permissions</strong> pour le détail des accès de chaque rôle.
          </div>
        </div>
        {error && <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3">{error}</div>}
      </form>
    </Modal>
  );
}

function CredentialRow({
  icon,
  label,
  value,
  k,
  copied,
  onCopy,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  k: string;
  copied: string | null;
  onCopy: (k: string, v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 bg-[#f5f8fa] rounded-lg p-2.5">
      <div className="w-7 h-7 rounded-md bg-white text-brand-700 flex items-center justify-center shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold">{label}</div>
        <div className="font-mono text-xs text-[#181c32] truncate">{value}</div>
      </div>
      <button onClick={() => onCopy(k, value)} className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-[#7e8299] hover:bg-white hover:text-brand-700">
        {copied === k ? <CheckCircle2 size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}
